<script>
import { createEventDispatcher, onDestroy, onMount } from "svelte";
import { getAuth } from 'firebase/auth'

import { collection, doc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore'
import { fiveLetterWords } from "../store/words";
import KeyboardButton from "./KeyboardButton.svelte";
import WordTile from "./WordTile.svelte";

const dispatch = createEventDispatcher()
const acceptableKeys = ['p','o','i','u','y','t','r','e','w','q','l','k','j','h','g','f','d','s','a','m','n','b','v','c','x','z','Backspace','Enter',]
export let challengeWord = ''
let status = ''
let ready = false
let letterStatus = []
let shake = false
let helpText = ''
let userId = undefined
let db = undefined
let challengeRef = undefined
let copyUrl = ''

const isWord = async(w) => {
    try {
      return $fiveLetterWords.findIndex(word => word === w) !== -1
    } catch (error) {
      return false
    }
  }

const handleInput = async(event) => {
  if (event.detail === 'delete' || event.detail === 'backspace') {
    status = ''
    letterStatus = []
    let split = challengeWord.split('')
    split.pop()
    challengeWord = split.join('')
  } else if (challengeWord.length < 5)
    challengeWord += event.detail
  if (challengeWord.length === 5) {
    const check = await isWord(challengeWord, true)
    status = check ? 'saved' : 'fail'
  }
}

const copyTextToClipboard = async(text) => {
  if (!navigator.clipboard) {
    helpText = 'Copy to clipboard failed'
    return
  }
  try {
    await navigator.clipboard.writeText(text)
    helpText = 'Link copied to clipboard. Share it with a friend and they can try guessing your word.'
  } catch (error) {
    helpText = 'Copy to clipboard failed'
  }
}

const createLink = async () => {
  if (status === 'saved') {
    if (!userId) userId = getAuth().currentUser.uid
    const hash = window.btoa(challengeWord + '+' + userId)
    const url = new URL(location.href)
    url.hash = hash
    try {
      if (!db) db = getFirestore()
      const userRef = doc(db, 'users', userId)
      const challengeQuery = query(collection(db, 'challenge'), where( "hash", '==', hash), where( "creator", '==', userRef))
      const querySnapshot = await getDocs(challengeQuery)
      if (querySnapshot.empty) {
        challengeRef = doc(collection(db, "challenge"))
        await setDoc(challengeRef, {
          creator: userRef,
          hashLink: hash,
        })
      } else {
        helpText = `Haven't you created this challenge already. `
      }
    } catch (error) {
      console.log(error)
      // what to do here
    }
    copyUrl = url.href
    copyTextToClipboard(url.href)
  } else {
    // or here
  }
}

const handleKeyDown = (ev) => {
  if (acceptableKeys.includes(ev.key))
    handleInput({ detail: ev.key.toLowerCase() })
}

onMount(() => {
  window.addEventListener('keydown', handleKeyDown)
})
onDestroy(() => {
  window.removeEventListener('keydown', handleKeyDown)
})

$: {
  if (status === 'saved') {
    ready = true
    letterStatus = challengeWord.split('').map(() => ({ exact: true }))
  } else if (status === 'checking') {
    const guessLetters = challengeWord.split('')
    letterStatus = guessLetters.map(() => {
      return {
        checking: true
      }
    })
  } else if (status === 'fail') {
    shake = true
    setTimeout(() => {
      shake = false
    }, 2000);
  }
}
</script>

<div class=" p-4">
  <div class="flex justify-center pb-4 max-w-[400px] mx-auto">
    <div class="text-xl text-teal-700">Enter a challenge word</div>
  </div>
  <div class={`flex items-center justify-center py-4${ shake ? ' shake' : '' }`}>
    <WordTile status={letterStatus[0] || {}} letter={challengeWord.charAt(0)} />
    <WordTile status={letterStatus[1] || {}} letter={challengeWord.charAt(1)} />
    <WordTile status={letterStatus[2] || {}} letter={challengeWord.charAt(2)} />
    <WordTile status={letterStatus[3] || {}} letter={challengeWord.charAt(3)} />
    <WordTile status={letterStatus[4] || {}} letter={challengeWord.charAt(4)} />
  </div>
  <div class="py4">
    <div class="grid grid-cols-10 gap-1 max-w-[400px] mx-auto py-1">
      {#each ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'] as letter, i}
        <KeyboardButton value={letter} on:input={handleInput} exact={[]} contains={[]} trash={[]} />
      {/each}
    </div>
    <div class="grid grid-cols-9 gap-1 max-w-[400px] mx-auto py-1 px-4">
      {#each ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'] as letter, i}
        <KeyboardButton value={letter} on:input={handleInput} exact={[]} contains={[]} trash={[]} />
      {/each}
    </div>
    <div class="grid grid-cols-11 gap-1 max-w-[400px] mx-auto py-1">
      <div class="flex items-center justify-center uppercase rounded-sm text-gray-200 cursor-pointer col-span-2 bg-teal-700 mr-2" on:click={() => handleInput({ detail: 'enter'})}>
        <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-backspace text-2xl" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentcolor" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M9 20v-8h-3.586a1 1 0 0 1 -.707 -1.707l6.586 -6.586a1 1 0 0 1 1.414 0l6.586 6.586a1 1 0 0 1 -.707 1.707h-3.586v8a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
        </svg>
      </div>
      {#each ['z', 'x', 'c', 'v', 'b', 'n', 'm'] as letter, i}
        <KeyboardButton value={letter} on:input={handleInput} exact={[]} contains={[]} trash={[]} />
      {/each}
      <div class="flex flex-col items-center justify-center uppercase rounded-sm text-gray-200 cursor-pointer col-span-2 bg-teal-700 ml-2" on:click={() => handleInput({ detail: 'delete'})}>
        <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-backspace text-3xl" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentcolor" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M20 6a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-11l-5 -5a1.5 1.5 0 0 1 0 -2l5 -5z" />
          <path d="M12 10l4 4m0 -4l-4 4" />
        </svg>
      </div>
    </div>
  </div>
  <div class="px-4 max-w-[400px] mx-auto">{helpText}</div>
  <div class="flex justify-end space-x-2 max-w-[400px] mx-auto">
    {#if copyUrl}
    <button class="rounded-sm bg-teal-700 text-white py-2 px-4 mt-4 disabled:opacity-40" on:click={copyTextToClipboard(copyUrl)}>Copy Link</button>
    <a href={copyUrl} target="_blank" class="rounded-sm bg-teal-700 text-white py-2 px-4 mt-4 disabled:opacity-40" on:click={copyTextToClipboard(copyUrl)}>Go to Link</a>
    {:else}
      <button class="rounded-sm bg-teal-700 text-white py-2 px-4 mt-4 disabled:opacity-40" disabled={!ready} on:click={createLink}>Create Custom Link</button>
    {/if}
    
  </div>
</div>

<style>
  .shake {
    animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
  }

@keyframes shake {
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }
  
  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }

  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
}
</style>